import { makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components';
import { KanbanDateLane, KanbanDateLaneOrder, QueryAggregate } from '@powerportalspro/core';
import { AuthStatus, useAuth, useT } from '@powerportalspro/react';
import { KanbanBoard, type ViewDefinition } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import { DemoShowcaseLayout } from './DemoShowcaseLayout';
import { companiesIn, MIDWEST, NORTHEAST, SOUTHEAST, SOUTHWEST, WEST, type DemoRegion } from './DemoRegion';

// The open opportunities of the demo's sample data, and a signed-in visitor's own, in lanes of when
// they're expected to close: calendar quarters behind and ahead of today, this fiscal year and the
// next, and everything before and after them. Each lane totals its estimated revenue and is in close-date order. The
// sample opportunities stay where they are: visitors can't change them. A signed-in visitor adds up to
// five opportunities of their own with a lane's Add button and moves them for real: a move changes the
// expected close date. A view for each region of the country narrows the board to the opportunities of
// companies there.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.KanbanBoardShowcase.KanbanBoardCloseDatesMainDemo';

const OPEN_OPPORTUNITIES_VIEW_ID = '5e8b1d2f-6a4c-4f7e-8b3d-9c2a1f0e7d64';

// A view of the open opportunities of each region's companies, beside the view of them all.
const REGION_VIEWS: { region: DemoRegion; viewId: string }[] = [
  { region: WEST, viewId: 'd398bac5-f6e7-40ac-b578-b00d662f327d' },
  { region: SOUTHWEST, viewId: 'a47c8f6d-bcb6-4ee2-a15d-8d2181539a15' },
  { region: MIDWEST, viewId: 'e00b6033-810d-405e-ad3e-d8d95054ed6d' },
  { region: SOUTHEAST, viewId: 'e2bf51b0-aa3a-4451-9945-26256de9d060' },
  { region: NORTHEAST, viewId: '0d359c58-af5e-4712-8d1b-d423f6f04267' },
];

// In date order, each starting where the one before it ends, with no day between them: last quarter,
// this quarter and the next, the rest of this fiscal year after them, the next fiscal year, and
// everything before and after.
const CLOSE_DATE_LANES = [
  KanbanDateLane.Earlier,
  KanbanDateLane.LastCalendarQuarter,
  KanbanDateLane.ThisCalendarQuarter,
  KanbanDateLane.NextCalendarQuarter,
  KanbanDateLane.ThisFiscalYear,
  KanbanDateLane.NextFiscalYear,
  KanbanDateLane.Later,
  KanbanDateLane.NoDate,
];

// The open opportunities — the sample data's, and a signed-in visitor's own, which the portal's table
// access adds — or those of a region's companies, ten cards a lane, each lane's estimated revenue in its
// header.
function openOpportunitiesView(id: string, displayName: string, region: DemoRegion | null): ViewDefinition {
  return {
    id,
    tableName: 'opportunity',
    displayName,
    pageSize: 10,
    fetchXml: `
      <fetch>
        <entity name="opportunity">
          <attribute name="name" />
          <attribute name="estimatedvalue" />
          <attribute name="estimatedclosedate" />
          <order attribute="estimatedclosedate" />
          <filter>
            <condition attribute="statecode" operator="eq" value="0" />
          </filter>
          ${region ? companiesIn(region, 'parentaccountid') : ''}
        </entity>
      </fetch>`,
    columns: [
      { columnName: 'name' },
      { columnName: 'estimatedvalue', aggregates: [{ aggregate: QueryAggregate.Sum, format: 'C0' }] },
      { columnName: 'estimatedclosedate' },
    ],
  };
}

const useStyles = makeStyles({
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function KanbanBoardCloseDatesMainSample() {
  const styles = useStyles();
  const t = useT();
  const auth = useAuth();
  const signedIn = auth.status === AuthStatus.Authenticated;

  // Built once, as a new list starts the board again.
  const views = useMemo(
    () => [
      openOpportunitiesView(OPEN_OPPORTUNITIES_VIEW_ID, t(`${STRINGS_BASE}.view-open-opportunities`), null),
      ...REGION_VIEWS.map(({ region, viewId }) => openOpportunitiesView(viewId, t(`${STRINGS_BASE}.view-${region.key}`), region)),
    ],
    [t],
  );

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <MessageBar intent="info">
            <MessageBarBody>{t(`${STRINGS_BASE}.${signedIn ? 'moves-saved' : 'sign-in-to-add'}`)}</MessageBarBody>
          </MessageBar>

          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-lanes`)} />
            <Item text={t(`${STRINGS_BASE}.try-fiscal`)} />
            <Item text={t(`${STRINGS_BASE}.try-add`)} />
            <Item text={t(`${STRINGS_BASE}.try-move`)} />
            <Item text={t(`${STRINGS_BASE}.try-past`)} />
            <Item text={t(`${STRINGS_BASE}.try-regions`)} />
          </ul>
        </>
      }
    >
      <KanbanBoard
        customViewDefinitions={views}
        defaultViewId={OPEN_OPPORTUNITIES_VIEW_ID}
        laneColumn="estimatedclosedate"
        dateLanes={CLOSE_DATE_LANES}
        dateLaneOrder={KanbanDateLaneOrder.Date}
        editable
        allowFiltering
        persistedStateQueryParameter="boardState"
        height="760px"
      />
    </DemoShowcaseLayout>
  );
}
